import { useCallback, useMemo, useState } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { useDropzone } from 'react-dropzone';
import {
  Mic,
  Upload,
  GraduationCap,
  Library,
  Loader2,
  Search,
  Check,
  Play,
  Pause,
} from 'lucide-react';
import { notify } from '@/lib/notify';
import { resolveApiUrl } from '@/providers/api-url';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

/**
 * Библиотека голосов для перевода эфиров.
 *
 * Голос берётся одним из трёх путей: готовый из каталога провайдера, клон по
 * записи урока или клон по загруженному файлу. Готовый — самый быстрый: он
 * доступен сразу и не требует согласия человека на копирование голоса.
 */

const apiBase = resolveApiUrl().replace(/\/$/, '');

interface VoiceProfile {
  id: number;
  name: string;
  kind: string;
  providerVoiceId: string;
  consentRecordedAt: string | null;
  archivedAt: string | null;
}

interface LessonSource {
  moduleItemId: number;
  title: string;
  courseTitle: string | null;
  hasAudioTrack: boolean;
  durationSeconds: number | null;
}

interface ProviderVoice {
  providerVoiceId: string;
  name: string;
  category: string;
  gender: string | null;
  language: string | null;
  description: string | null;
  previewUrl: string | null;
  alreadyAdded: boolean;
}

const KIND_LABELS: Record<string, string> = {
  CLONE: 'Клон',
  PRESET: 'Готовый',
  NEUTRAL: 'Нейтральный',
};

const GENDER_LABELS: Record<string, string> = {
  male: 'мужской',
  female: 'женский',
  neutral: 'нейтральный',
};

type SourceTab = 'catalog' | 'lesson' | 'upload';

function formatDuration(seconds: number | null): string {
  if (!seconds) {
    return '';
  }
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `${minutes} мин` : `${seconds} с`;
}

export function VoiceLibraryPanel({ canEdit }: { canEdit: boolean }) {
  const { query } = useCustom<VoiceProfile[]>({
    url: `${apiBase}/admin/voice-profiles`,
    method: 'get',
  });

  const profiles = useMemo(() => query.data?.data ?? [], [query.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [tab, setTab] = useState<SourceTab>('catalog');
  const [voiceName, setVoiceName] = useState('');
  const [consent, setConsent] = useState(false);
  const [lessonSearch, setLessonSearch] = useState('');
  const [selectedLesson, setSelectedLesson] = useState<number | null>(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [selectedCatalogVoice, setSelectedCatalogVoice] = useState<ProviderVoice | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { query: lessonsQuery } = useCustom<LessonSource[]>({
    url: `${apiBase}/admin/voice-profiles/lesson-sources`,
    method: 'get',
    config: { query: lessonSearch ? { search: lessonSearch } : {} },
    queryOptions: { enabled: dialogOpen && tab === 'lesson' },
  });

  const lessons = useMemo(() => lessonsQuery.data?.data ?? [], [lessonsQuery.data]);

  const { query: catalogQuery } = useCustom<ProviderVoice[]>({
    url: `${apiBase}/admin/voice-profiles/provider-catalog`,
    method: 'get',
    queryOptions: { enabled: dialogOpen && tab === 'catalog' },
  });

  const catalogVoices = useMemo(() => {
    const all = catalogQuery.data?.data ?? [];
    const needle = catalogSearch.trim().toLowerCase();
    if (!needle) {
      return all;
    }
    return all.filter((voice) =>
      [voice.name, voice.description ?? '', voice.language ?? '']
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [catalogQuery.data, catalogSearch]);

  const { mutate: runMutation } = useCustomMutation();

  const resetForm = useCallback(() => {
    setVoiceName('');
    setConsent(false);
    setSelectedLesson(null);
    setLessonSearch('');
    setCatalogSearch('');
    setSelectedCatalogVoice(null);
    setPreviewId(null);
    setBusy(false);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    resetForm();
  }, [resetForm]);

  /** Проигрывание образца: одновременно звучит только один голос. */
  const togglePreview = useCallback(
    (voice: ProviderVoice) => {
      if (!voice.previewUrl) {
        return;
      }
      const existing = document.getElementById('voice-preview-player');
      if (existing instanceof HTMLAudioElement) {
        existing.pause();
        existing.remove();
      }
      if (previewId === voice.providerVoiceId) {
        setPreviewId(null);
        return;
      }
      const player = new Audio(voice.previewUrl);
      player.id = 'voice-preview-player';
      player.addEventListener('ended', () => setPreviewId(null));
      document.body.append(player);
      void player.play().catch(() => setPreviewId(null));
      setPreviewId(voice.providerVoiceId);
    },
    [previewId],
  );

  const handleAddFromCatalog = useCallback(() => {
    if (!selectedCatalogVoice) {
      return;
    }
    setBusy(true);
    runMutation(
      {
        url: `${apiBase}/admin/voice-profiles/from-catalog`,
        method: 'post',
        values: {
          providerVoiceId: selectedCatalogVoice.providerVoiceId,
          name: voiceName.trim() || selectedCatalogVoice.name,
        },
      },
      {
        onSuccess: () => {
          notify.success('Голос добавлен в библиотеку');
          closeDialog();
          void query.refetch();
          void catalogQuery.refetch();
        },
        onError: (error) => {
          setBusy(false);
          notify.error(error?.message ?? 'Не удалось добавить голос');
        },
      },
    );
  }, [selectedCatalogVoice, voiceName, runMutation, query, catalogQuery, closeDialog]);

  const handleCloneFromLesson = useCallback(() => {
    if (!selectedLesson || voiceName.trim().length === 0) {
      return;
    }
    setBusy(true);
    runMutation(
      {
        url: `${apiBase}/admin/voice-profiles/clone-from-lesson`,
        method: 'post',
        values: {
          moduleItemId: selectedLesson,
          name: voiceName.trim(),
          consentRecorded: true,
        },
      },
      {
        onSuccess: () => {
          notify.success('Голос создан по записи урока');
          closeDialog();
          void query.refetch();
        },
        onError: (error) => {
          setBusy(false);
          notify.error(error?.message ?? 'Не удалось создать голос');
        },
      },
    );
  }, [selectedLesson, voiceName, runMutation, query, closeDialog]);

  // Файл уходит провайдеру напрямую, поэтому обычный загрузчик медиатеки
  // здесь не подходит — он кладёт файл в наше хранилище.
  const handleUpload = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file || voiceName.trim().length === 0) {
        return;
      }
      setBusy(true);
      try {
        const form = new FormData();
        form.append('name', voiceName.trim());
        form.append('consentRecorded', 'true');
        form.append('file', file);

        const response = await fetch(`${apiBase}/admin/voice-profiles/clone-from-upload`, {
          method: 'POST',
          body: form,
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Сервер ответил ${response.status}`);
        }

        notify.success('Голос создан по загруженной записи');
        closeDialog();
        void query.refetch();
      } catch (error) {
        setBusy(false);
        notify.error(error instanceof Error ? error.message : 'Загрузка не удалась');
      }
    },
    [voiceName, query, closeDialog],
  );

  const uploadReady = voiceName.trim().length > 0 && consent && !busy;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleUpload,
    accept: { 'audio/*': [], 'video/*': [] },
    maxFiles: 1,
    disabled: !uploadReady,
  });

  /** Что мешает нажать кнопку — подсказка вместо молчаливо серой кнопки. */
  const lessonBlocker = useMemo(() => {
    if (voiceName.trim().length === 0) {
      return 'Укажите название голоса';
    }
    if (!consent) {
      return 'Подтвердите согласие на использование голоса';
    }
    if (selectedLesson === null) {
      return 'Выберите урок из списка';
    }
    return null;
  }, [voiceName, consent, selectedLesson]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Голоса для перевода
          </CardTitle>
          <CardDescription>
            Голос, которым будет звучать перевод. Можно взять готовый из каталога, из записи
            урока или из своего файла.
          </CardDescription>
        </div>
        <Button onClick={() => setDialogOpen(true)} disabled={!canEdit}>
          Добавить голос
        </Button>
      </CardHeader>

      <CardContent>
        {profiles.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Голосов пока нет. Самый быстрый способ — выбрать готовый из каталога.
          </p>
        ) : (
          <div className="divide-y">
            {profiles.map((profile) => (
              <div key={profile.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{profile.name}</span>
                    <Badge variant="outline">{KIND_LABELS[profile.kind] ?? profile.kind}</Badge>
                    {profile.archivedAt && <Badge variant="secondary">В архиве</Badge>}
                  </div>
                  <p className="truncate font-mono text-xs text-muted-foreground">
                    {profile.providerVoiceId}
                    {profile.consentRecordedAt ? ' · согласие получено' : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setDialogOpen(true);
          } else {
            closeDialog();
          }
        }}
      >
        <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>Новый голос</DialogTitle>
            <DialogDescription>
              Готовый голос доступен сразу. Клон повторяет конкретного человека — для него нужна
              запись речи и согласие.
            </DialogDescription>
          </DialogHeader>

          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value === 'lesson' || value === 'upload' ? value : 'catalog')}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="px-6 pt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="catalog" disabled={busy} className="gap-2">
                  <Library className="h-4 w-4" />
                  Готовые
                </TabsTrigger>
                <TabsTrigger value="lesson" disabled={busy} className="gap-2">
                  <GraduationCap className="h-4 w-4" />
                  Из урока
                </TabsTrigger>
                <TabsTrigger value="upload" disabled={busy} className="gap-2">
                  <Upload className="h-4 w-4" />
                  Файл
                </TabsTrigger>
              </TabsList>
            </div>

            {/* ---- Готовые голоса ---- */}
            <TabsContent value="catalog" className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={catalogSearch}
                    onChange={(event) => setCatalogSearch(event.target.value)}
                    placeholder="Поиск по имени, языку или описанию"
                    className="pl-8"
                    disabled={busy}
                  />
                </div>

                <ScrollArea className="h-[320px] rounded-md border">
                  {catalogQuery.isLoading ? (
                    <div className="flex h-[320px] items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : catalogQuery.isError ? (
                    <p className="p-6 text-center text-sm text-muted-foreground">
                      Каталог недоступен. Проверьте ключ ElevenLabs в интеграциях.
                    </p>
                  ) : catalogVoices.length === 0 ? (
                    <p className="p-6 text-center text-sm text-muted-foreground">
                      Ничего не найдено
                    </p>
                  ) : (
                    <div className="divide-y">
                      {catalogVoices.map((voice) => {
                        const selected =
                          selectedCatalogVoice?.providerVoiceId === voice.providerVoiceId;
                        return (
                          <div
                            key={voice.providerVoiceId}
                            className={[
                              'flex items-center gap-3 px-3 py-2.5 transition',
                              selected ? 'bg-accent' : 'hover:bg-accent/50',
                            ].join(' ')}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCatalogVoice(voice);
                                if (voiceName.trim().length === 0) {
                                  setVoiceName(voice.name.split(' - ')[0] ?? voice.name);
                                }
                              }}
                              disabled={busy || voice.alreadyAdded}
                              className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:opacity-60"
                            >
                              <span
                                className={[
                                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                                  selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40',
                                ].join(' ')}
                              >
                                {selected && <Check className="h-3 w-3" />}
                              </span>
                              <span className="min-w-0">
                                <span className="flex flex-wrap items-center gap-1.5">
                                  <span className="truncate text-sm font-medium">{voice.name}</span>
                                  {voice.alreadyAdded && (
                                    <Badge variant="secondary">уже добавлен</Badge>
                                  )}
                                </span>
                                <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                                  {voice.language && (
                                    <Badge variant="outline">{voice.language}</Badge>
                                  )}
                                  {voice.gender && (
                                    <span>{GENDER_LABELS[voice.gender] ?? voice.gender}</span>
                                  )}
                                  {voice.description && (
                                    <span className="truncate">· {voice.description}</span>
                                  )}
                                </span>
                              </span>
                            </button>

                            {voice.previewUrl && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0"
                                onClick={() => togglePreview(voice)}
                                title="Послушать образец"
                              >
                                {previewId === voice.providerVoiceId ? (
                                  <Pause className="h-4 w-4" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>

                <div className="space-y-2">
                  <Label>Название в библиотеке</Label>
                  <Input
                    value={voiceName}
                    onChange={(event) => setVoiceName(event.target.value)}
                    placeholder={selectedCatalogVoice?.name ?? 'Подставится из выбранного голоса'}
                    disabled={busy}
                  />
                </div>
              </div>
            </TabsContent>

            {/* ---- Клон из урока ---- */}
            <TabsContent value="lesson" className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Название голоса</Label>
                  <Input
                    value={voiceName}
                    onChange={(event) => setVoiceName(event.target.value)}
                    placeholder="Например: Ведущий Иван"
                    disabled={busy}
                  />
                </div>

                <label className="flex items-start gap-2.5 rounded-md border p-3 text-sm">
                  <Checkbox
                    checked={consent}
                    onCheckedChange={(value) => setConsent(value === true)}
                    disabled={busy}
                    className="mt-0.5"
                  />
                  <span className="leading-snug">
                    Человек, чей голос используется, дал согласие на его копирование и озвучку
                    переводов.
                  </span>
                </label>

                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={lessonSearch}
                    onChange={(event) => setLessonSearch(event.target.value)}
                    placeholder="Поиск урока по названию"
                    className="pl-8"
                    disabled={busy}
                  />
                </div>

                <ScrollArea className="h-[240px] rounded-md border">
                  {lessonsQuery.isLoading ? (
                    <div className="flex h-[240px] items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : lessons.length === 0 ? (
                    <p className="p-6 text-center text-sm text-muted-foreground">
                      Уроков со звуком не найдено
                    </p>
                  ) : (
                    <div className="divide-y">
                      {lessons.map((lesson) => (
                        <button
                          key={lesson.moduleItemId}
                          type="button"
                          onClick={() => setSelectedLesson(lesson.moduleItemId)}
                          disabled={busy}
                          className={[
                            'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition',
                            selectedLesson === lesson.moduleItemId
                              ? 'bg-accent'
                              : 'hover:bg-accent/50',
                          ].join(' ')}
                        >
                          <span
                            className={[
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                              selectedLesson === lesson.moduleItemId
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-muted-foreground/40',
                            ].join(' ')}
                          >
                            {selectedLesson === lesson.moduleItemId && <Check className="h-3 w-3" />}
                          </span>
                          <span className="min-w-0 flex-1 truncate">{lesson.title}</span>
                          <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                            {lesson.hasAudioTrack && <Badge variant="outline">аудио</Badge>}
                            {formatDuration(lesson.durationSeconds)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>

                <p className="text-xs text-muted-foreground">
                  Из записи берётся полторы минуты речи начиная с тридцатой секунды — заставка
                  в образец не попадает.
                </p>
              </div>
            </TabsContent>

            {/* ---- Клон из файла ---- */}
            <TabsContent value="upload" className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Название голоса</Label>
                  <Input
                    value={voiceName}
                    onChange={(event) => setVoiceName(event.target.value)}
                    placeholder="Например: Ведущий Иван"
                    disabled={busy}
                  />
                </div>

                <label className="flex items-start gap-2.5 rounded-md border p-3 text-sm">
                  <Checkbox
                    checked={consent}
                    onCheckedChange={(value) => setConsent(value === true)}
                    disabled={busy}
                    className="mt-0.5"
                  />
                  <span className="leading-snug">
                    Человек, чей голос используется, дал согласие на его копирование и озвучку
                    переводов.
                  </span>
                </label>

                <div
                  {...getRootProps()}
                  className={[
                    'flex flex-col items-center gap-2 rounded-md border-2 border-dashed p-8 text-center text-sm transition',
                    isDragActive ? 'border-primary bg-accent' : 'border-muted',
                    uploadReady ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
                  ].join(' ')}
                >
                  <input {...getInputProps()} />
                  {busy ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <Upload className="h-6 w-6 text-muted-foreground" />
                  )}
                  <span>
                    {voiceName.trim().length === 0
                      ? 'Сначала укажите название голоса'
                      : !consent
                        ? 'Подтвердите согласие на использование голоса'
                        : 'Перетащите запись или нажмите для выбора'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Аудио или видео, до 25 МБ. Достаточно полутора минут чистой речи.
                  </span>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="flex-row items-center justify-between gap-3 border-t px-6 py-4">
            <span className="text-xs text-muted-foreground">
              {tab === 'catalog'
                ? selectedCatalogVoice
                  ? `Выбран: ${selectedCatalogVoice.name}`
                  : 'Выберите голос из списка'
                : tab === 'lesson'
                  ? (lessonBlocker ?? 'Готово к созданию')
                  : ''}
            </span>

            <div className="flex gap-2">
              <Button variant="outline" onClick={closeDialog} disabled={busy}>
                Отмена
              </Button>
              {tab === 'catalog' && (
                <Button
                  onClick={handleAddFromCatalog}
                  disabled={!selectedCatalogVoice || busy}
                  className="gap-2"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Добавить голос
                </Button>
              )}
              {tab === 'lesson' && (
                <Button
                  onClick={handleCloneFromLesson}
                  disabled={lessonBlocker !== null || busy}
                  className="gap-2"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Создать из урока
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
